import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  downloadPublicSharedVideo,
  getPublicSharedVideo,
  type PublicSharedVideo,
} from '@/features/sharing/services/sharingApi';
import { Button, DownloadIcon, Spinner, ValoiVLockup } from '@/shared/ui';

export default function PublicSharePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [video, setVideo] = useState<PublicSharedVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setVideo(null);
    setDownloadError(false);
    void getPublicSharedVideo(token)
      .then((result) => {
        if (!active) return;
        setVideo(result);
      })
      .catch(() => {
        if (!active) return;
        setVideo(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      const url = await downloadPublicSharedVideo(token);
      window.location.href = url;
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="share-page">
      <header className="share-page__brand">
        <Link to="/" aria-label="Valoi home">
          <ValoiVLockup />
        </Link>
      </header>

      {loading ? (
        <div className="share-page__state">
          <Spinner size={26} label="Loading shared video" />
        </div>
      ) : !video ? (
        <div className="share-page__state" role="alert">
          <h1>Video unavailable</h1>
          <p>This share link is no longer available.</p>
        </div>
      ) : (
        <article className="share-page__viewer">
          <div className="share-page__title-row">
            <h1>{video.title}</h1>
            <Button iconLeft={<DownloadIcon size={16} />} onClick={() => void handleDownload()} disabled={downloading}>
              {downloading ? 'Preparing…' : 'Download'}
            </Button>
          </div>
          {downloadError ? (
            <p className="share-page__error" role="alert">
              Could not prepare the download.
            </p>
          ) : null}
          <video className="share-page__video" src={video.videoUrl} poster={video.thumbnailUrl} controls playsInline />
        </article>
      )}
    </main>
  );
}
